import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import './BuyingTemplateIndicator.scss';
import { CloseIcon } from '../common/CloseIcon';

interface BuyingTemplateIndicatorProps {
  hasTemplate: boolean;
  shouldSaveTemplate: boolean;
  onToggleSaveTemplate: (value: boolean) => void;
  isLoadingTemplate?: boolean;
  isTemplateAnimating?: boolean;
}

const BuyingTemplateIndicator: React.FC<BuyingTemplateIndicatorProps> = ({
  hasTemplate,
  shouldSaveTemplate,
  onToggleSaveTemplate,
  isLoadingTemplate = false,
  isTemplateAnimating = false,
}) => {
  const { t } = useTranslation();
  
  // Temporary fallback translations (replace with actual i18n keys when available)
  const translations = {
    loading: t('building.offMarket.template.loading', 'Loading template...'),
    usingTemplate: t('building.offMarket.template.usingTemplate', 'Using saved template'),
    clear: t('building.offMarket.template.clear', 'Clear template'),
    update: t('building.offMarket.template.update', 'Update template'),
    save: t('building.offMarket.template.save', 'Save as template'),
    updateTemplate: t('building.offMarket.template.updateTemplate', 'Update template'),
    saveAsTemplate: t('building.offMarket.template.saveAsTemplate', 'Save as template'),
    moreInfo: t('building.offMarket.template.moreInfo', 'More info'),
    tooltipText: t('building.offMarket.template.tooltipText', 'Save these preferences for future use in other buildings')
  };
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState<boolean>(false);

  const handleToggle = () => {
    const newValue = !shouldSaveTemplate;
    onToggleSaveTemplate(newValue);

    // Show confirmation animation when enabling
    if (newValue) {
      setShowSaveConfirmation(true);
      setTimeout(() => setShowSaveConfirmation(false), 2000);
    }
  };

  if (isLoadingTemplate) {
    return (
      <div className="template-indicator template-indicator--loading">
        <div className="template-indicator__spinner" />
        <span>{translations.loading}</span>
      </div>
    );
  }

  return (
    <div className={`template-indicator ${isTemplateAnimating ? 'template-indicator--animating' : ''}`}>
      <div className={`template-toggle ${shouldSaveTemplate ? 'template-toggle--active' : ''} ${showSaveConfirmation ? 'template-toggle--saved' : ''}`}>
        <label className="template-toggle__label">
          <input
            type="checkbox"
            className="template-toggle__checkbox"
            checked={shouldSaveTemplate}
            onChange={handleToggle}
            aria-label={hasTemplate ? translations.update : translations.save}
          />
          <div className="template-toggle__switch">
            <div className="template-toggle__switch-handle" />
          </div>
          <span className="template-toggle__text">
            {hasTemplate 
              ? translations.updateTemplate
              : translations.saveAsTemplate
            }
          </span>
        </label>
        
        <button
          className="template-toggle__info"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={showTooltip ? () => setShowTooltip(false) : () => setShowTooltip(true)}
          aria-label={translations.moreInfo}
        >
          <svg
            fill="none"
            height="16"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
            id="fi_9446643"
            stroke="currentColor"
          >
            <g fill="currentColor" stroke="currentColor"><path
              d="m12.75 11c0-.4142-.3358-.75-.75-.75s-.75.3358-.75.75v6c0 .4142.3358.75.75.75s.75-.3358.75-.75z">
            </path><path clipRule="evenodd"
              d="m12 1.25c-5.93706 0-10.75 4.81294-10.75 10.75 0 5.9371 4.81294 10.75 10.75 10.75 5.9371 0 10.75-4.8129 10.75-10.75 0-5.93706-4.8129-10.75-10.75-10.75zm-9.25 10.75c0-5.10863 4.14137-9.25 9.25-9.25 5.1086 0 9.25 4.14137 9.25 9.25 0 5.1086-4.1414 9.25-9.25 9.25-5.10863 0-9.25-4.1414-9.25-9.25z"
              fillRule="evenodd">
              </path><path d="m13 8c0 .55228-.4477 1-1 1s-1-.44772-1-1 .4477-1 1-1 1 .44772 1 1z"></path></g>
          </svg>
        </button>
        
        {showTooltip && (
          <div className="template-toggle__tooltip">
            {translations.tooltipText}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyingTemplateIndicator;
